import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core'
import { IxAvatar, IxButton, IxDropdownItem, ToastService } from '@siemens/ix-angular/standalone'
import { TranslatePipe } from '@ngx-translate/core'
import { sha256 } from 'js-sha256'
import { AuthService } from './auth.contract'
import { LoginExpirationWarningComponent } from './login-expiration-warning/login-expiration-warning.component'

@Component({
  selector: 'user-auth',
  imports: [IxButton, IxAvatar, IxDropdownItem, TranslatePipe, LoginExpirationWarningComponent],
  providers: [ToastService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (isLoggingIn() !== true) {
      <ix-button icon="log-in" (click)="onLoginClicked()">{{ 'LOGIN.LOGIN_BUTTON' | translate }}</ix-button>
    } @else {
      @let expirationTime = this.authService.expirationDateTime();
      @if (expirationTime) {
        <login-expiration-warning [expirationTime]="expirationTime" (logout)="authService.logout()" />
      }
      <ix-avatar [extra]="userEmail()" [image]="gravatar()" [initials]="initials()" [username]="userName()">
        <ix-dropdown-item icon="log-out" [label]="'LOGIN.LOGOUT_BUTTON' | translate" (click)="authService.logout()" />
      </ix-avatar>
    }
  `,
})
export class UserAuthComponent {
  authService = inject(AuthService)

  isLoggingIn(): boolean | undefined {
    return this.authService.isAuthenticated()
  }

  readonly userName = computed(() => {
    return this.authService.userProfile()?.displayName ?? ''
  })

  readonly initials = computed(() => this.authService.userProfile()?.initials ?? '')

  readonly userEmail = computed(() => this.authService.userProfile()?.email ?? '')

  /**
   * Gravatar URL for the user's email address. Fallback to generic image if no email is set or the user has no gravatar.
   */
  readonly gravatar = computed(() => {
    const email = this.authService.userProfile()?.email.trim().toLowerCase() ?? ''
    const hash = sha256(email)
    return `https://www.gravatar.com/avatar/${hash}?d=mp`
  })

  onLoginClicked() {
    this.authService.login()
  }
}
